/**
 * THE Personal-Area string catalogue. RU is the only locale (adopted answer **ST13**:
 * i18n from day one, RU the only complete locale; EN/KZ stay translation tasks).
 *
 * ⚠ EVERY user-visible string lives here and none is inline in a component — the rule
 * `AGENTS.md` states for the whole product ("Russian-first labels via i18n keys"). That
 * includes the CURRENCY SYMBOL, the MONTH NAMES and every code→label table
 * (`src/money/labels.ts` resolves them), because a helper module is exactly where a
 * "temporary" inline label survives for years.
 *
 * ⚠ WHAT IS DELIBERATELY ABSENT, so nobody "completes" the catalogue by adding it:
 *
 *   - A LANGUAGE SWITCH. `ui-enduser-personal-area` §1 shows Русский / English /
 *     Қазақша; ST13 ships RU only, so a switch would offer one option — a control the
 *     subscriber can press with no effect, which is this project's D109/D211 class.
 *   - ~~«ПОПОЛНИТЬ» / «ОПЛАТИТЬ» and every other word for a payment this build cannot
 *     make.~~ *(struck 2026-08-12 — **W3 BUILT THEM**: the card top-up, SBP, the saved
 *     card, the auto-payment switch and the promised-payment form. The suspension notice
 *     now names the figure AND the action, which is the whole point of the wave.)*
 *   - ~~«АКТИВИРОВАТЬ» for an account awaiting activation (W4/D45). The honest STATUS
 *     label ships; the button follows with the flow behind it.~~ *(struck 2026-08-12 —
 *     **W4 BUILT IT**: the button sits in the account card's status corner, gated on
 *     `self_activation_enabled` from `GET /pa/accounts/capabilities`.)*
 *   - A **DURATION or «до какого числа»** field on the block form. That is template-based
 *     blocking (F20), which is POST-LAUNCH and has no entity anywhere in this system —
 *     and an `until` would create the paired auto-activation row whose cancellation is the
 *     trap the cancel route refuses. Ending a block early is «Разблокировать».
 *   - A **CONTACT field on the confirmation-code step**. `POST …/{kind}/code` takes no
 *     address by design (the code goes to the card's own contact or nowhere), so a box
 *     there would look like it chose the destination while the server ignored it.
 *   - A **«Тарифный план» column** on «Блокировки», which §13A-1 draws. Tariff CHANGE is
 *     not built, so the word would promise a section that does not exist — and the tariff
 *     is already on the dashboard's account card, which is one press away.
 *   - **DEVICE REGISTRATION and any MAC field.** A device is registered on the ENROLLMENT
 *     portal, where its identity context is known; a portal registration would have to
 *     take a MAC from the request, which is the device-hijack primitive this system has
 *     already closed (D23).
 *   - A **PASSWORD DISPLAY** for an Account. The column is recoverable by deliberate
 *     policy, so putting it on this internet-facing listener would be a live credential on
 *     the wire. The form asks him to TYPE the old one.
 *   - «ПОДКЛЮЧИТЬ АВТОПЛАТЕЖИ» **as a checkbox on the payment form**. §8b lists one
 *     beside «Запомнить карту», and the backend deliberately did not build it: it would
 *     need a column on `payonline_order` and a branch in the callback, while the
 *     auto-payment TOGGLE beside the saved card reaches the same state. A checkbox whose
 *     value the server drops is a consent the subscriber believes he gave — the D109/D211
 *     class, and worse here than usual.
 *   - A RECEIPT-CONTACT INPUT. §8b draws «Номер телефона» and «Email» as fields; the wire
 *     carries them as a DISPLAY, because `internal/fiscal` resolves the delivery contact
 *     from the subscriber's card at receipt time and `payonline.StartInput` has no contact
 *     field at all. An editable box would be a field nothing reads, and he would believe
 *     he had redirected a fiscal document.
 *   - A FEE WARNING on the promised-payment form («За подключение обещанного платежа
 *     будет списана плата»). §2.7 says a fee «may be implemented as a charged service»,
 *     and `PromiseTermsFor` carries no fee field — so the figure would have to be invented
 *     here. Where an operator charges one it appears on «Расход средств» as the charge it
 *     is.
 *   - A CHARSET HINT on the password form. C18 is the server's rule and the server's
 *     message (`pa.password_charset`) is what the screen shows; a second copy here is
 *     a copy that will diverge from the one that must hold.
 *   - Any per-outcome recovery wording. `recover.result` is ONE text for every outcome
 *     (match, no match, unconfirmed contact, archived card) because a branch there is a
 *     subscriber-enumeration oracle — Appendix L §4 F2 struck exactly that branch on
 *     2026-08-11. It carries the support hint INSIDE itself for the same reason.
 *   - «ОСТАЛОСЬ ВЫПЛАТИТЬ» for an installment. The remaining amount is a decaying SHIELD
 *     and the whole price was debited on day one (Appendix M §3.18 «M3»); that wording
 *     would tell the subscriber he owes money already taken from him.
 */
const ru = {
  brand: 'Личный кабинет',

  common: {
    loading: 'Загрузка…',
    retry: 'Повторить',
    required: 'Заполните поле',
    networkError: 'Нет связи с сервером. Проверьте подключение и повторите попытку.',
    unavailableTitle: 'Личный кабинет недоступен',
    unavailableText: 'Не удалось получить данные. Повторите попытку через минуту.',
    // The file-size units (P2). They live in `common` because `format.ts` holds no Russian
    // word at all — it formats DIGITS and returns which unit they are in, and the catalogue
    // is the only place this app keeps a label (ST13).
    bytes: 'Б',
    kilobytes: 'КБ',
    megabytes: 'МБ',
    // The two traffic scales (P3). A subscriber's month spans three units, which is exactly
    // why the wire carries OCTETS and the portal chooses the unit — a server that pre-divided
    // would have had to pick one for every scale from a kilobyte to a terabyte.
    gigabytes: 'ГБ',
    terabytes: 'ТБ',
  },

  /**
   * THE SECTION-MENU GROUPS (2026-08-14 — the rework that removed the horizontal scrollbar).
   *
   * ⚠ NOT ONE OF THESE FIVE WORDS IS ALSO A `navLabel`, and that is a rule and not a
   * coincidence — `nav.test.tsx` fails if it is ever broken. A parent named «Оплата» whose
   * first child is «Оплата», or a parent «Поддержка» whose first child is «Поддержка», is a
   * menu a subscriber reads twice and trusts less; it is also indistinguishable to a screen
   * reader, which announces the button and the link with the same accessible name.
   *
   * The five come from `ui-enduser-personal-area`'s own «Navigation subtree» — the tree the
   * operator's existing subscribers have used for years — with the three departures `nav.ts`
   * argues for. No word is decorated with «раздел» or a caret glyph: the caret is drawn in
   * CSS and the ROLE is carried by `aria-expanded` on a real `<button>`, so a screen reader
   * already announces «Платежи, свёрнуто, кнопка» without this catalogue inventing a suffix
   * that would then have to be translated three ways.
   */
  nav: {
    groups: {
      pay: 'Платежи',
      bill: 'Счёт',
      service: 'Услуги',
      help: 'Помощь',
      settings: 'Настройки',
    },
  },

  login: {
    title: 'Вход в личный кабинет',
    lead: 'Введите логин и пароль личного кабинета.',
    loginLabel: 'Логин',
    loginPlaceholder: 'Введите логин',
    // The support hint the UI spec's own field note asks for (§1, «The PORTAL login
    // issued by staff — subscriber.login, not an Account login»). P7-A1 records the
    // consequence the operator lives with: a subscriber holds TWO credentials, so the
    // screen has to say which one to type. It is a hint under the field and nothing
    // more — the enrollment portal is not linked, described or offered here.
    loginHint:
      'Это логин личного кабинета, выданный оператором. Логин учётной записи, которым регистрируют устройство, здесь не подойдёт.',
    passwordLabel: 'Пароль',
    passwordPlaceholder: 'Введите пароль',
    submit: 'Войти',
    forgot: 'Забыли пароль?',
    // The operator's terms-of-use link (`GET /pa/config`, `terms_url`). The LABEL is
    // ours; the destination is his, and it is rendered only when he has set one.
    // ⚠ A third-party destination will not open for a subscriber suspended for
    // non-payment — the walled garden learns only the acquirer's own domains — which is
    // the registry entry's own warning and not something this screen can repair.
    terms: 'Условия использования',
  },

  recover: {
    title: 'Восстановление пароля',
    lead: 'Укажите логин и контакт, записанный в договоре. На этот контакт будет отправлен новый пароль.',
    loginLabel: 'Логин',
    loginPlaceholder: 'Введите логин',
    contactLabel: 'Email или телефон, указанный в договоре',
    contactPlaceholder: 'ivanov@example.ru или +7 912 000 00 01',
    submit: 'Отправить',
    back: 'Вернуться к входу',
    resultTitle: 'Запрос принят',
    // ⚠ ONE WORDING FOR EVERY OUTCOME. Do not split this into a success text and a
    // failure text, and do not echo the submitted login or contact into it — either
    // change turns a public form into a test for which logins and contacts exist.
    result:
      'Если указанные логин и контакт совпадают с данными договора, новый пароль отправлен на этот контакт — в SMS или на email, в зависимости от того, что вы указали. Если сообщение не пришло, обратитесь в поддержку по телефону, указанному в договоре.',
  },

  password: {
    navLabel: 'Изменение пароля',
    title: 'Изменение пароля',
    lead: 'Пароль от личного кабинета. Пароли учётных записей меняются отдельно.',
    currentLabel: 'Текущий пароль',
    newLabel: 'Новый пароль',
    confirmLabel: 'Подтверждение пароля',
    mismatch: 'Пароли не совпадают',
    submit: 'Сменить пароль',
    success: 'Пароль изменён. Остальные сеансы завершены.',
  },

  // The identity facts and the two prompts. They keep the `home.` prefix they shipped
  // under in W1b: the keys are referenced by the dashboard now, and renaming a key to
  // match a screen's new name is a rename that buys nothing and breaks a translation.
  home: {
    // Rendered as the dashboard's LEAD line, under §3's own title «Мои аккаунты». The
    // interpolation is guarded by strings.test.ts: dropping «{{name}}» loses the subscriber's
    // name with no error anywhere, which is the silent half of that defect class.
    greeting: 'Здравствуйте, {{name}}',
    loginFact: 'Логин личного кабинета',
    sessionFact: 'Сеанс активен до',
    changePassword: 'Изменить пароль',
    temporaryTitle: 'Смените пароль',
    temporaryText: 'Пароль выдан оператором и помечен как временный. Задайте свой пароль.',
    ipRestrictedTitle: 'Вход только с ваших адресов',
    ipRestrictedText:
      'Оператор разрешил вход в личный кабинет только с IP-адресов ваших учётных записей. Из другой сети вход не выполнится.',
  },

  // ---- shared money vocabulary ------------------------------------------------------
  money: {
    contractLabel: 'Договор',
    noContracts:
      'На вашей карточке нет действующих договоров. Обратитесь в поддержку по телефону, указанному в договоре.',
    previousMonth: 'Предыдущий месяц',
    nextMonth: 'Следующий месяц',

    // The pager. §22's history and §9's month are capped by the SERVER at a fixed page,
    // and until this wave the screen showed the first page and said nothing — «the newest
    // 500 of 812», with no control that could reach the rest.
    pager: {
      shown: 'Показано {{from}}–{{to}} из {{total}}',
      all: 'Всего записей: {{total}}',
      previous: 'Предыдущие',
      next: 'Следующие',
    },

    // §2.3's contract status. Only the non-active values are ever rendered — an active
    // contract carries no badge, because a badge saying «действует» on every screen is
    // noise that trains the eye to skip the one that matters.
    contractStatus: {
      terminated: 'Расторгнут',
    },
    contractTerminatedNote:
      'Договор расторгнут. Данные показаны для истории; обслуживание по нему не ведётся.',

    // The currency symbols the spec prints in its own examples («999,00 руб»).
    currency: {
      RUB: 'руб',
      USD: '$',
      EUR: '€',
    },

    month: {
      '01': 'Январь',
      '02': 'Февраль',
      '03': 'Март',
      '04': 'Апрель',
      '05': 'Май',
      '06': 'Июнь',
      '07': 'Июль',
      '08': 'Август',
      '09': 'Сентябрь',
      '10': 'Октябрь',
      '11': 'Ноябрь',
      '12': 'Декабрь',
    },

    // Appendix C §2.2, as a subscriber reads it. «Ожидает активации» is the DERIVED
    // marker and it overrides status 10 — without it a brand-new account reads as
    // «отключено», i.e. as something broken.
    accountStatus: {
      active: 'Активна',
      financial: 'Приостановлена за неоплату',
      userBlock: 'Приостановлена по вашей заявке',
      adminBlock: 'Заблокирована оператором',
      trafficBlock: 'Приостановлена по лимиту трафика',
      disabled: 'Отключена',
      awaiting: 'Ожидает активации',
    },

    // ⚠ The suspended value must NOT read «Обслуживание приостановлено»: that is the
    // suspension notice's own title, and two elements carrying one wording make every
    // `getByText` in the suite ambiguous — which is how a test starts asserting on the wrong
    // element. This column is about the расчётный период, so it says so.
    cycleState: {
      paid: 'Период оплачен',
      unpaid: 'Период не оплачен',
      suspended: 'Период приостановлен',
    },

    // The engine's own suspension reasons. They are kept apart because
    // `service_charge_failed` is the one that is NOT about the subscription: the amount
    // owed belongs to a service, and telling the subscriber otherwise sends him looking
    // at his tariff.
    reason: {
      charge_failed: 'не хватило средств на списание абонентской платы',
      service_charge_failed: 'не хватило средств на списание за подключённую услугу',
      missed_promise: 'обещанный платёж не был погашен в срок',
      unknown: 'обслуживание приостановлено',
    },

    // The two cases where the arithmetic a subscriber would do in his head gives the
    // wrong answer. An empty value means «no explanation is owed».
    //
    // ⚠ THE MISSED-PROMISE SENTENCE NAMED THE WRONG ALLOWANCE UNTIL 2026-08-12. It said
    // «в пределы кредитного лимита», which is what Amendment 01 §4.3 said before the
    // customer's decision of 2026-08-06 struck it in place: `billing.missedPromiseTopUp`
    // is `−(balance + credit_limit + INSTALLMENT)`, because an outstanding installment is
    // protection the operator SOLD against exactly this negative balance and a missed
    // promise does not repeal it. Quoting the narrower rule told a subscriber under a
    // 3 000 shield that he owed the whole shield back; the figure beside the sentence was
    // right the whole time, which is why nothing failed. (The old test asserted the string
    // was PRESENT, not that it was TRUE.)
    floor: {
      missed_promise:
        'Обещанный платёж не погашен в срок. Чтобы возобновить обслуживание, баланс должен вернуться в разрешённый минус — это кредитный лимит вместе с действующим покрытием рассрочки. Период при этом не списывается повторно, он уже оплачен.',
      withheld:
        'Один из периодов удержан: он не был списан и не был оказан, поэтому в сумму входит и резерв под продление. После поступления денег период будет списан целиком и отсчёт начнётся с этого момента.',
    },

    billingModel: {
      A: 'ежедневное списание',
      B: 'календарный месяц',
      C: 'период с даты оплаты',
    },

    // §3.3's three methods. The figure means a different thing in each, and a screen that
    // shows it without the method cannot label it.
    recommendedMode: {
      '0': 'стоимость тарифа и услуг за месяц',
      '1': 'стоимость за месяц за вычетом баланса',
      '2': 'сумма, необходимая для возобновления обслуживания',
      other: 'рекомендуемая сумма пополнения',
    },
  },

  // ---- §3 the dashboard --------------------------------------------------------------
  dashboard: {
    navLabel: 'Мои аккаунты',
    title: 'Мои аккаунты',

    balance: 'Баланс',
    creditLimit: 'Кредитный лимит',
    creditLimitHint: 'Разрешённый отрицательный баланс — он входит в доступные средства.',
    promise: 'Обещанный платёж',
    promiseHint: 'Остаток обещанного платежа входит в доступные средства, но не в баланс.',
    promiseUntil: 'Действует до {{when}}. Поступающие деньги сначала погашают его.',
    installmentShield: 'Покрытие рассрочки',
    installmentShieldHint:
      'Не долг: стоимость уже списана при оформлении. Эта сумма защищает списания и уменьшается по графику.',
    availability: 'Доступные средства',
    availabilityHint: 'Баланс, кредитный лимит, остаток обещанного платежа и покрытие рассрочки.',
    reserved: 'Зарезервировано под продление',
    reservedHint:
      'Сумма, отложенная под ближайшее списание. Резерв не блокирует обслуживание — его тратит само продление.',
    reservedUntil: 'Резерв действует до списания {{when}}.',
    spendable: 'Свободно для разовых покупок',
    spendableHint: 'Доступные средства за вычетом резерва под продление.',
    recommended: 'Рекомендуемый платёж',
    // The primary action of the landing page from W3 onwards: «what do I owe» is
    // answered by the block above it, and this is the button that acts on the answer.
    payAction: 'Пополнить счёт',
    contractNumber: 'Договор',
    paymentCode: 'Код оплаты',
    paymentCodeHint: 'Этот код называют в офисе и вводят в терминале.',

    accountsTitle: 'Учётные записи',
    noAccounts: 'На этом договоре нет учётных записей.',
    tariff: 'Тариф',
    speed: 'до {{kbps}} Кбит/с',
    subscriptionFee: 'Абонентская плата',
    subscriptionFeeHint: 'Цена тарифа без учёта скидки за привязанную карту.',

    // ⚠ THIS ROW WAS «СЛЕДУЮЩИЙ ПЛАТЁЖ» UNTIL 2026-08-12, AND THE SERVER WAS RIGHT — the
    // LABEL was wrong. `cycle_cost` is §3.3 mode 0: the cost of one period at current
    // prices, priced at status ACTIVE on purpose (so it does not collapse to zero for the
    // weeks a subscriber spends in a vacation block on a tariff with no block price) and
    // with own-anchor services AMORTIZED over the period rather than counted as the fires
    // that fall inside it. So it is a cost of ownership and not the next debit: a dacha
    // account on a winter block was quoted the full tariff for a period the engine will
    // charge the block price for — or nothing at all — and an account with an independent
    // periodic service is quoted more than its boundary will take. The figure is the
    // engine's and stays; what it is CALLED is now what it is.
    cycleCost: 'Стоимость периода',
    cycleCostHint:
      'Тариф и привязанные услуги за расчётный период по текущим ценам, со скидкой. Это не сумма ближайшего списания: она отличается, когда обслуживание приостановлено или у услуги свой срок списания.',
    modelAMonthly: 'указана стоимость за месяц, списывается по дням',

    // The DATE, on its own row. It is the engine's `min(run_at)` over this account's
    // pending CHARGE timers and nothing else, and it is empty while the period is
    // suspended — which is the honest answer, and the one §2.4 asks for.
    nextChargeDate: 'Следующее списание',
    nextChargeNone: 'не запланировано',

    // ⚠ «ОПЛАЧЕНО ДО» IS AN ASSERTION ABOUT A PAYMENT and it must not be made about a
    // period nobody paid for. Reproduced live: a subscriber who paid 0,00 and was
    // suspended at activation read «Оплачено до 05.09.2026» directly under «Внесите
    // 450,00». The same date is a true and useful fact — it is the end of the current
    // billing period — so the row keeps the date and changes what it claims.
    coverageUntil: 'Оплачено до',
    coverageModelAHint: 'При ежедневном списании оплачен текущий день.',
    cycleUntil: 'Расчётный период до',
    cycleUntilHint: 'Конец текущего расчётного периода. Период ещё не оплачен.',
    cycleState: 'Расчётный период',

    // The ACTIVATION GATE's own two figures, on a never-activated account. Neither
    // «Стоимость периода» above nor the contract's «Рекомендуемый платёж» can activate
    // him — both were measured at 500,00 against a 1 000,00 minimum on the demo seed.
    activationRequired: 'Нужно для активации',
    activationRequiredHint:
      'Сумма, при которой учётную запись можно активировать. Другие суммы на этой странице её не заменяют.',
    activationShortfall: 'Не хватает',
    activationShortfallHint: 'Столько нужно внести, чтобы достичь суммы активации.',

    // ⚠ THE ACTION W4 ADDS, in the account card's status corner — where §12 puts it («new
    // accounts show an "Activate" button in the Status column»). It is rendered only when
    // the operator has enabled self-activation (`GET /pa/accounts/capabilities`), and a
    // failed or pending capability read is treated as «off»: a button whose only possible
    // answer is `409 pa.activation_disabled` is a dead control.
    activate: 'Активировать',
    activation: {
      title: 'Активировать учётную запись?',
      // What it does, in the order it happens. The charge is the ENGINE's — the first
      // period is priced and taken exactly as it is for an account staff activates — so no
      // figure is quoted here; «Нужно для активации» and «Не хватает» are already on the
      // card above, and quoting a third number in a dialog is how two surfaces come to
      // disagree.
      what: 'Обслуживание начнётся сразу, а с договора будет списана стоимость первого расчётного периода по тарифу этой учётной записи.',
      network: 'Доступ откроется автоматически в течение нескольких минут — устройству нужно переподключиться к сети.',
      confirm: 'Активировать',
      back: 'Отмена',
      done: 'Учётная запись активирована. Доступ откроется в течение нескольких минут.',

      // THE PLAN CHOICE (customer, 2026-08-16) — shown only when the Account was set up
      // against a tariff GROUP and therefore has no plan of its own yet.
      plansLabel: 'Выберите тарифный план',
      plansLabelNamed: 'Выберите тарифный план — {{group}}',
      plansLoading: 'Загружаем тарифные планы…',
      // The two figures, and they answer different questions: what the activation debits
      // today, and what the plan costs every period afterwards.
      planChargeNow: 'Списание сейчас',
      planCadenceMonthly: 'Далее, в месяц',
      planCadenceDaily: 'Далее, в месяц (списывается по дням)',
      planCadencePeriod: 'Далее, за период',
      planShortfall: 'Не хватает',
      planSpeedMbit: '{{value}} Мбит/с',
      planSpeedKbit: '{{value}} кбит/с',
      // charge_known === false. NEVER rendered as 0,00 ₽.
      planPriceUnknown: 'уточняйте',
      plansNoneTitle: 'Тарифные планы недоступны',
      plansNoneBody:
        'Для этой учётной записи пока не настроены тарифные планы. Обратитесь в техническую поддержку — мы всё быстро поправим.',
      plansNoneArchived:
        'Набор тарифных планов для этой учётной записи больше не действует. Обратитесь в техническую поддержку — мы подберём тариф.',
    },

    suspension: {
      title: 'Обслуживание приостановлено',
      // The label the whole screen exists for: it says what paying this figure ACHIEVES.
      payLabel: 'Внесите эту сумму, чтобы обслуживание возобновилось',
      contractWide:
        'Сумма указана по договору целиком: она возобновляет все перечисленные ниже учётные записи. Обслуживание восстановится автоматически в течение нескольких минут после поступления денег — заявка не нужна.',
      requirementLabel: 'Начислено к оплате',
      requirementHint:
        'Полная стоимость периода вместе с привязанными услугами. Сумма к внесению меньше или больше её: она учитывает деньги на договоре и то, что поступающий платёж сначала погашает обещанный.',
      accountsTitle: 'Что приостановлено',
      since: 'с {{when}}',
      // ⚠ THE ACTION W3 ADDS, AND THE REASON THE WAVE EXISTS. Until 2026-08-12 the notice
      // named the figure and offered NO WAY TO PAY IT — «Пополнить» was on
      // `nav.test.tsx`'s forbidden list precisely because the screen behind it did not
      // exist. It does now, and the secondary link beside it is §8e's, so a subscriber
      // who cannot pay by card is one press from the alternative rather than one
      // telephone call.
      payAction: 'Оплатить',
      otherMethods: 'Другие способы оплаты',
    },

    // ⚠ THE SCREEN USED TO FALL SILENT HERE. The notice is shown only when a payment
    // actually changes something — at least one suspended period AND a non-zero figure —
    // and three ordinary paths give zero while the status column is still red. In all
    // three the page showed «Приостановлена за неоплату» with no notice, no amount and no
    // sentence, which is indistinguishable from a page that is working. The server now
    // names WHY the figure is absent, and these are the two sentences.
    noFigure: {
      awaitingTitle: 'Оплата получена, обслуживание возобновляется',
      awaitingText:
        'Деньги на договоре уже есть. Обслуживание восстановится автоматически в течение нескольких минут — заявка не нужна. Если через полчаса ничего не изменилось, обратитесь в поддержку.',
      unavailableTitle: 'Сумма к оплате не рассчитана',
      unavailableText:
        'Обслуживание приостановлено, но рассчитать сумму для возобновления не удалось. Обратитесь в поддержку по телефону, указанному в договоре, — назовите номер договора.',
    },
  },

  // ---- §8b/§8d/§10/§10a the payment section (W3) ---------------------------------------
  //
  // ⚠ THE ONE STRING THAT IS NOT HERE IS «ЗАПОМНИТЬ КАРТУ»'S CAPTION. It is built by the
  // SERVER (`personalarea.saveCardCaption`) and arrives as `save_card_caption`, and the
  // portal renders it VERBATIM. `answers.md` P7-P1 makes it a requirement with three
  // named parts — what may be charged and when, that it earns the recurring-method
  // discount at the configured percentage, that it can be removed at any time and where —
  // precisely because the box is PRE-TICKED, so the consent is given by NOT acting and
  // the caption is the only text he is guaranteed to have seen. A copy in this catalogue
  // would be one rebuild away from being shortened to «Запомнить карту» by somebody who
  // has not read P7-P1, and nothing would fail.
  topup: {
    navLabel: 'Оплата',
    title: 'Оплата',
    lead: 'Пополнение баланса банковской картой или через СБП. Деньги поступают на договор в течение нескольких минут.',

    balance: 'Баланс',
    amountLabel: 'Сумма',
    amountPlaceholder: '0,00',
    amountInvalid: 'Укажите сумму в рублях и копейках, например 450,00',
    amountTooSmall: 'Минимальная сумма платежа — {{min}}',
    // The two sentences under the prefilled figure. The first is said only when the
    // contract is suspended, and it is the sentence the whole wave exists for.
    prefillResume:
      'Подставлена сумма, при поступлении которой обслуживание возобновится. Её можно изменить.',
    prefillRecommended: 'Подставлена рекомендуемая сумма. Её можно изменить.',

    payCard: 'Оплатить картой',
    paySBP: 'Оплатить через СБП',
    cardOnAcquirer:
      'Данные карты вводятся на защищённой странице банка. Личный кабинет их не получает и не хранит.',
    redirectTitle: 'Открываем страницу оплаты',
    redirectText:
      'Если страница банка не открылась автоматически, перейдите к оплате по ссылке ниже. Ссылка действует ограниченное время.',
    redirectLink: 'Перейти к оплате',

    receiptTitle: 'Кассовый чек',
    receiptHint:
      'Чек будет отправлен на контакты, записанные в договоре. Изменить их можно через поддержку.',
    receiptEmail: 'Email',
    receiptPhone: 'Телефон',

    otherMethods: 'Другие способы оплаты',
    otherMethodsHint: 'Как заплатить в офисе или через терминал.',

    // Two different facts and therefore two different sentences — see
    // `paymentsBlockedLabel`. Merging them would tell a company to try later.
    blockedTitle: 'Оплата в личном кабинете сейчас недоступна',
    blocked: {
      // ⚠ THE KEY IS THE SERVER'S REASON VALUE AND IT COVERS TWO KINDS OF PAYER, WHICH IS WHY
      // THIS SENTENCE NAMES BOTH. `payments_blocked_reason: "legal_entity"` is what
      // `PortalProfile.PaysInPortal` returns for a company AND for a SOLE PROPRIETOR — an
      // `is_entrepreneur` card whose `kind` is «physical» — because Appendix L §3 groups the
      // two («Balance top-ups are available to individuals only. Legal entities and sole
      // proprietors have the Payment items hidden by default»). The server sends one value for
      // both, so the portal cannot tell them apart and ONE sentence has to be true of both.
      //
      // ~~«Оплата в личном кабинете открыта только для физических лиц.»~~ STRUCK 2026-08-12
      // (Phase 7 combination review). It was FALSE to the sole proprietor reading it: an ИП is
      // a физическое лицо, so the screen refused him for not being the thing he is, and it
      // left him nothing to act on. The rule was never wrong — only this wording, which was
      // written for a company. It survived because EVERY fixture on this surface uses
      // `kind = legal`, where the sentence happens to be true: a fixture that cannot express
      // the difference its own test is named for.
      legal_entity:
        'Для организаций и индивидуальных предпринимателей оплата в личном кабинете отключена. Оплатите по счёту или обратитесь в поддержку по телефону, указанному в договоре.',
      acquirer_unavailable:
        'Оплата картой и через СБП временно не настроена. Счёт можно пополнить в офисе или через терминал — раздел «Другие способы оплаты».',
      other:
        'Оплатить в личном кабинете не получится. Счёт можно пополнить в офисе или через терминал — раздел «Другие способы оплаты».',
    },

    // §8d — the QR and the wait for the bank's own confirmation (adopted answer S17).
    sbp: {
      title: 'Оплата через СБП',
      instructions:
        'Откройте приложение банка, отсканируйте код или перейдите по ссылке и подтвердите платёж.',
      openLink: 'Открыть в приложении банка',
      qrAlt: 'QR-код для оплаты через СБП',
      expires: 'Код действует до {{when}}',
      amount: 'К оплате',
      refresh: 'Проверить оплату',
      restart: 'Оформить новый платёж',
      // The QR is issued for one payment and is never saved as a method.
      note: 'Платёж через СБП разовый: карта при этом не сохраняется и автоплатёж по нему не подключается.',
    },

    // §8d step 3 — the states an order passes through.
    order: {
      stateLabel: 'Состояние платежа',
      state: {
        pending: 'Ожидание подтверждения банка',
        paid: 'Оплачено',
        failed: 'Платёж не выполнен',
        expired: 'Срок оплаты истёк',
      },
      pendingText:
        'Платёж ещё не подтверждён банком. Обычно это занимает несколько секунд; страницу можно не закрывать.',
      paidText:
        'Деньги поступили на договор. Если обслуживание было приостановлено, оно возобновится в течение нескольких минут.',
      expiredText: 'Код не был оплачен вовремя. Оформите новый платёж.',
      // §8b's three sentences, keyed by the acquirer's own error code. The wording is
      // deliberately the same as the server's own landing page, so a subscriber who saw
      // one and then the other is not told two different things about one decline.
      failure: {
        '1': 'Техническая ошибка на стороне платёжной системы. Повторите оплату немного позже.',
        '2': 'Оплата этой картой невозможна. Попробуйте другую карту или другой способ оплаты.',
        '3': 'Банк, выпустивший карту, отклонил платёж. Свяжитесь с банком или используйте другую карту.',
        other: 'Платёж не прошёл. Попробуйте ещё раз или используйте другой способ оплаты.',
      },
    },

    // §10a — the saved method, and §10 — the proactive monthly charge.
    savedCard: {
      title: 'Сохранённый способ оплаты',
      // The label of the mask ROW. It is deliberately NOT `title`: that wording is the
      // block's heading and also appears inside the pre-ticked consent caption, and three
      // elements with one wording make every text query in the suite ambiguous.
      pan: 'Карта',
      none: 'Карта не сохранена. Отметьте «Запомнить карту» при оплате — тогда сюда попадёт способ оплаты, которым можно пользоваться дальше.',
      holder: 'Держатель карты',
      acquirer: 'Платёжная система',
      savedAt: 'Сохранена',
      statusLabel: 'Состояние',
      status: {
        active: 'Действует',
        expired: 'Недействительна',
      },
      reasonLabel: 'Причина',
      reason: {
        card_expired: 'истёк срок действия карты',
        anchor_invalid: 'банк отклонил привязку карты',
      },
      // The re-save prompt S17 asks for, on an anchor that died at the acquirer.
      expiredTitle: 'Карта больше не работает',
      expiredText:
        'Сохраните карту заново, чтобы автоплатежи и автоматические списания продолжали работать: оплатите любую сумму и оставьте отметку «Запомнить карту».',
      expiredAction: 'Сохранить карту заново',
      // An ACTIVE method the system cannot actually charge is an installation fault, and
      // the screen must not promise a monthly charge it cannot make.
      notChargeable:
        'Списания по этой карте сейчас невозможны. Обратитесь в поддержку по телефону, указанному в договоре.',
      unlink: 'Отвязать карту',
      unlinkTitle: 'Отвязать карту?',
      // The three warnings §10a's confirmation dialog requires, before he confirms.
      unlinkWarnAuto: 'Автоплатежи и автоматические попытки списания при задолженности прекратятся.',
      unlinkWarnManual:
        'Пополнять счёт нужно будет вручную — картой при оплате или другим способом, пока вы не сохраните новую карту.',
      unlinkWarnAnchor:
        'Привязка карты будет удалена у платёжной системы: списать по ней что-либо станет невозможно.',
      unlinkWarnDiscount:
        'Скидка за привязанную карту перестанет действовать со следующего списания; текущий период не пересчитывается.',
      unlinkConfirm: 'Отвязать',
      unlinkCancel: 'Отмена',
      unlinked: 'Карта отвязана.',
    },

    autoPay: {
      label: 'Ежемесячный автоплатёж',
      hint: 'Сумма рассчитывается автоматически и списывается с сохранённой карты в последний день месяца.',
      // §10's own separation, stated where a subscriber decides: turning the switch off
      // is NOT the same as unlinking the card, and he must be able to tell them apart.
      separation:
        'Выключение автоплатежа не отвязывает карту: доплата при неудачном списании и попытки списания при задолженности продолжат работать. Чтобы прекратить все списания по инициативе оператора, отвяжите карту.',
      enabled: 'Автоплатёж включён.',
      disabled: 'Автоплатёж выключен.',
    },
  },

  // ---- §8e other payment methods (W3) --------------------------------------------------
  instructions: {
    navLabel: 'Другие способы оплаты',
    title: 'Другие способы оплаты',
    lead: 'Как оплатить услуги, если оплата картой невозможна.',
    paymentCode: 'Код оплаты',
    paymentCodeHint: 'Этот код называют в офисе и вводят в терминале.',
    // `contract.payment_code` is `NOT NULL DEFAULT ''` with no generator, so on an
    // imported estate it is blank — the W2 review's finding E8. The page renders anyway
    // and says what to name instead.
    noCode: 'Код оплаты по договору не назначен — назовите номер договора: {{number}}.',
    empty:
      'Оператор пока не разместил инструкции по оплате. Обратитесь в поддержку по телефону, указанному в договоре.',
  },

  // ---- §6 account state --------------------------------------------------------------
  state: {
    navLabel: 'Состояние счёта',
    title: 'Состояние счёта',
    lead: 'Баланс договора и движение денег в текущем месяце.',
    balance: 'Баланс',
    period: 'Месяц',
    received: 'Поступившие платежи',
    receivedHint:
      'Сумма всех платежей месяца. Аннулированный платёж и его сторнирование входят обоими знаками и в сумме дают ноль.',
    expenses: 'Расход',
    expensesHint: 'Списания, отнесённые к этому расчётному месяцу.',
  },

  // ---- §9 fund usage -----------------------------------------------------------------
  usage: {
    navLabel: 'Расход средств',
    title: 'Расход средств',
    lead: 'Списания за выбранный расчётный месяц.',
    periodLabel: 'Период',
    accountLabel: 'Учётная запись',
    allAccounts: 'Все учётные записи',
    totalCharged: 'Общая сумма списания',
    totalChargedHint: 'За весь месяц, а не только за показанные строки.',
    columnName: 'Списание',
    columnAccount: 'Учётная запись',
    columnCoverage: 'Период оказания',
    columnQuantity: 'Количество',
    columnAmount: 'Сумма',
    empty: 'За этот месяц списаний нет.',
    chargeType: {
      subscription_fee: 'абонентская плата',
      usage: 'потребление',
      service: 'услуга',
      connection: 'подключение',
    },
  },

  // ---- §22 payment history -----------------------------------------------------------
  payments: {
    // ⚠ «ПЛАТЕЖИ» UNTIL 2026-08-12, renamed when W3 put a section that TAKES a payment
    // beside it. «Оплата» and «Платежи» in one strip are two words a subscriber reads as
    // one thing; the screen's own title has always been «История платежей».
    navLabel: 'История платежей',
    title: 'История платежей',
    lead: 'Платежи и корректировки по договору. Аннулирование и возврат показаны отдельными строками — так устроен учёт: запись не изменяется, а компенсируется.',
    fromLabel: 'С даты',
    toLabel: 'По дату',
    apply: 'Показать',
    columnDate: 'Дата',
    columnNumber: 'Номер платежа',
    columnSource: 'Способ платежа',
    columnAmount: 'Сумма',
    columnState: 'Состояние',
    empty: 'За выбранный период платежей нет.',
    autoCharge: 'списано с привязанной карты автоматически',
    cancelledAt: 'аннулирован {{when}}',
    state: {
      accepted: 'Принят',
      cancelled: 'Аннулирован',
    },
    source: {
      manual: 'В офисе',
      document: 'По счёту',
      registry: 'Реестр банка',
      import: 'Импорт платёжной системы',
      external: 'Терминал или платёжная система',
      // ⚠ TWO WORDINGS FOR ONE `source`, chosen by DIRECTION. A funds transfer writes a
      // debit on the contract the money leaves and a credit on the one it reaches, both
      // `source = "transfer"` and neither carrying a correction kind — so until
      // 2026-08-12 money LEAVING a subscriber's contract was described to him as money
      // arriving.
      transfer: 'Перевод с другого договора',
      transferOut: 'Перевод на другой договор',
      prepaid_card: 'Карта оплаты',
      auto_charge: 'Списание с привязанной карты',
      correction: 'Корректировка',
      payonline: 'Банковская карта',
      osmp: 'Терминал',
    },
    // The sentence that makes an append-only ledger readable: WHAT this row is.
    correction: {
      reversalOf: {
        annul: 'Сторнирование: платёж аннулирован.',
        amount: 'Сторнирование: сумма платежа исправлена, ниже — исправленная запись.',
        transfer: 'Сторнирование: платёж переведён на другой договор.',
        other: 'Сторнирование платежа.',
      },
      creditOf: {
        amount: 'Исправленная сумма платежа.',
        transfer: 'Платёж, переведённый с другого договора.',
        restore: 'Платёж восстановлен.',
        other: 'Корректирующая запись.',
      },
    },
    refund: {
      requested:
        'Возврат на карту отправлен в банк. Деньги приходят обычно за 3–10 банковских дней.',
      settled: 'Возврат на карту исполнен банком.',
      failed: 'Возврат на карту не исполнен. Обратитесь в поддержку.',
    },
  },

  // ---- §21 the promised-payment FORM + §23 the history ---------------------------------
  promises: {
    navLabel: 'Обещанные платежи',
    title: 'Обещанные платежи',
    lead: 'Обещанный платёж откладывает оплату: услуги продолжают работать, а сумму нужно внести до указанного срока.',

    // §21's form. Every figure is `PromiseTermsFor`'s — the ENFORCEMENT's own decision,
    // from the same function that refuses the grant — so this panel cannot offer what the
    // service would refuse.
    formTitle: 'Оформить обещанный платёж',
    balance: 'Баланс',
    minAmount: 'Минимальная сумма',
    maxAmount: 'Максимальная сумма',
    // 0 on the wire means «no upper bound», and printing «0,00» would assert a limit that
    // does not exist — the one place on this screen a zero must not be rendered as money.
    maxAmountNone: 'не ограничена',
    termDays: 'Срок',
    termDaysValue: '{{days}} дн.',
    repayBy: 'Погасить до',
    repayByHint: 'Дату назначает оператор; изменить её в личном кабинете нельзя.',
    recommended: 'Рекомендуемая сумма',
    recommendedUse: 'Подставить',
    amountLabel: 'Сумма',
    amountPlaceholder: '0,00',
    amountInvalid: 'Укажите сумму в рублях и копейках, например 300,00',
    amountOutOfRange: 'Сумма должна быть от {{min}} до {{max}}',
    amountBelowMin: 'Сумма не может быть меньше {{min}}',
    submit: 'Оформить',
    granted: 'Обещанный платёж оформлен.',
    // The operator's own switch: §2.9's «allow by client» is a different flag from «allow
    // by manager», so this sentence must not say the product does not exist.
    notAllowed:
      'По вашему договору обещанный платёж в личном кабинете не оформляется. Уточните условия в поддержке по телефону, указанному в договоре.',
    blockedTitle: 'Сейчас оформить обещанный платёж нельзя',
    // The five refusals `GrantPromiseFor` would produce, worded for a subscriber. The
    // panel shows the sentence and hides the button, so the screen never offers what the
    // server refuses.
    blocked: {
      'contracts.promise_active_exists':
        'По договору уже действует обещанный платёж. Новый можно оформить после того, как он будет погашен.',
      'contracts.promise_not_allowed':
        'Обещанный платёж не предусмотрен условиями вашей группы. Уточните в поддержке.',
      'contracts.promise_amount_out_of_range':
        'Сумма обещанного платежа выходит за допустимые пределы.',
      'contracts.promise_contract_too_new':
        'Обещанный платёж пока недоступен: договор оформлен слишком недавно.',
      'contracts.promise_cooldown':
        'После предыдущего обещанного платежа должно пройти время. Попробуйте позже.',
      other: 'Оформить обещанный платёж сейчас нельзя. Уточните в поддержке.',
    },
    historyTitle: 'История',
    columnGranted: 'Запрошен',
    columnExpires: 'Срок действия',
    columnAmount: 'Сумма',
    columnRepaid: 'Погашено',
    columnRemainder: 'Остаток',
    columnState: 'Статус',
    empty: 'Обещанных платежей по этому договору не было.',
    takenBySelf: 'Оформлен вами в личном кабинете.',
    takenByStaff: 'Оформлен оператором.',
    // The ENGINE's four states, all first-class: «погашен» and «просрочен» are the pair a
    // subscriber most needs to tell apart, and §23's own table had them in a parenthesis.
    state: {
      active: 'Действует',
      expired: 'Просрочен',
      repaid: 'Погашен',
      cancelled: 'Отменён',
    },
  },

  // ---- §20 installments --------------------------------------------------------------
  installments: {
    navLabel: 'Рассрочка',
    title: 'Рассрочка',
    lead: 'Оформленные рассрочки и их графики. Рассрочку оформляет оператор по вашей заявке.',
    empty: 'Рассрочек по этому договору нет.',
    plan: 'План рассрочки',
    duration: 'на {{months}} мес.',
    price: 'Полная стоимость',
    priceHint: 'Списана целиком при оформлении.',
    shield: 'Действующее покрытие',
    shieldHint:
      'Это не долг. Стоимость уже оплачена, а покрытие защищает списания и уменьшается на один шаг в каждую дату графика.',
    assigned: 'Оформлена',
    closedAt: 'закрыта {{when}}',
    live: 'Действует',
    closed: 'Завершена',
    scheduleTitle: 'График',
    columnDue: 'Дата платежа',
    columnAmount: 'Стоимость',
    columnApplied: 'Состояние',
    appliedAt: 'учтено {{when}}',
    pending: 'ещё не наступила',
    noSteps: 'График не сформирован.',
  },

  // ---- §19 documents -----------------------------------------------------------------
  documents: {
    navLabel: 'Документы',
    title: 'Ежемесячный счёт',
    lead: 'Документы, которые оператор открыл для личного кабинета.',
    fromLabel: 'Период (с)',
    toLabel: 'Период (по)',
    stateLabel: 'Состояние',
    stateAll: 'Все',
    stateUnpaid: 'Не оплаченные',
    statePaid: 'Оплаченные',
    columnDate: 'Дата',
    columnNumber: 'Номер документа',
    columnKind: 'Документ',
    columnAmount: 'Сумма',
    columnState: 'Состояние',
    columnAction: 'Действие',
    download: 'Скачать',
    downloading: 'Загрузка…',
    downloadFailed: 'Не удалось скачать документ',
    empty: 'За выбранный период документов нет.',
    // `vat_amount` and `payable` were both on the wire and neither was rendered: a legal
    // entity could not see the VAT the server had already sent him, and an act — a
    // document that was never payable — read «Без оплаты», i.e. exactly like an unpaid
    // invoice.
    vat: 'в том числе НДС',
    state: {
      paid: 'Оплачен',
      unpaid: 'Не оплачен',
      notPayable: 'Оплата не требуется',
    },
  },

  // ---- §4 the profile: contacts and their CONFIRMATION (W4, F4) ------------------------
  //
  // ⚠ THE LEAD SAYS WHAT AN UNCONFIRMED CONTACT COSTS, and it is not a scare sentence: the
  // notification engine refuses an unconfirmed contact per channel, so «Забыли пароль?»
  // reports success and sends nothing, and no suspension notice, payment confirmation or
  // dunning warning is delivered either. At cutover that is every imported subscriber.
  // A screen that said only «подтвердите контакт» gives him no reason to.
  profile: {
    navLabel: 'Профиль',
    title: 'Профиль',
    lead: 'Контакты для сообщений от оператора и для восстановления пароля.',
    nameLabel: 'ФИО',
    loginLabel: 'Логин личного кабинета',
    // Display only: the column has no confirmation and nothing is ever delivered to it, so
    // there is no editor and no «Подтвердить» beside it.
    phoneLabel: 'Контактный телефон',
    phoneHint: 'Номер из договора. Сообщения на него не отправляются; изменить его можно через поддержку.',
    mobileLabel: 'Мобильный телефон',
    mobileHint: 'На этот номер приходят SMS: код подтверждения, новый пароль при восстановлении, уведомления об оплате и приостановке.',
    emailLabel: 'Email',
    emailHint: 'На этот адрес приходят письма: код подтверждения, новый пароль при восстановлении, уведомления и чеки.',
    notFilled: 'не указан',
    notFilledEmail: 'не указан',
    confirmed: 'Подтверждён',
    unconfirmed: 'Не подтверждён',
    // The one sentence that turns a badge into a reason to act.
    unconfirmedHint: 'Пока контакт не подтверждён, сообщения на него не отправляются — в том числе новый пароль при восстановлении.',
    add: 'Добавить',
    edit: 'Изменить',
    confirm: 'Подтвердить',
    save: 'Сохранить',
    cancel: 'Отмена',
    mobilePlaceholder: '+7 912 000 00 01',
    emailPlaceholder: 'ivanov@example.ru',
    valueRequired: 'Укажите контакт',
    // ⚠ RULE 5 — the portal password, asked ONLY when the contact actually changes (added
    // 2026-08-12 with the server's `current_password` requirement). The hint says WHY in one
    // sentence, because a password box that appears on a profile form and explains nothing
    // reads as a fault; and it says what the box is — the PORTAL password, not the account
    // password of «Мои учётные записи», which is a different credential on the same screen's
    // neighbour and is the mistake this wording exists to prevent.
    passwordLabel: 'Пароль от личного кабинета',
    passwordHint: 'Смена контакта подтверждается паролем: на новый адрес уходит код, и по нему можно восстановить доступ к кабинету. Это пароль от личного кабинета, а не от учётной записи.',
    passwordRequired: 'Введите пароль от личного кабинета',
    // Editing a contact RESETS its confirmation (F4), and the screen renders the state the
    // SERVER returned rather than the value it sent — so this sentence is shown for a
    // contact that came back unconfirmed, and the shorter one below when it came back
    // confirmed (a re-save of the same value keeps it).
    saved: 'Контакт сохранён. Подтвердите его, чтобы получать сообщения.',
    savedConfirmed: 'Контакт сохранён.',
    codeSentTitle: 'Код отправлен',
    codeSent: 'Код отправлен {{channel}}. Код действует до {{when}}.',
    // ⚠ THE SENTENCE THAT REPLACES AN ADDRESS FIELD. There is none, deliberately.
    codeNoAddress: 'Код уходит на контакт, указанный выше. Указать другой адрес здесь нельзя: если контакт изменился, сначала измените его и подтвердите заново.',
    codeLabel: 'Код из сообщения',
    codePlaceholder: '000000',
    codeRequired: 'Введите код',
    codeSubmit: 'Применить',
    codeResend: 'Отправить код повторно',
    confirmedNow: 'Контакт подтверждён.',
    channel: {
      sms: 'в SMS',
      email: 'на email',
    },
    // `notifier_available: false` ⇒ «Подтвердить» is not rendered at all, because the two
    // code routes answer 503. This is the sentence in its place: an explanation is not a
    // control.
    notifierUnavailable: 'Оператор пока не настроил отправку сообщений, поэтому подтвердить контакт в личном кабинете нельзя. Обратитесь в поддержку по телефону, указанному в договоре.',
  },

  // ---- §13A voluntary blocking: «отпуск» (W4, F19/F21) ---------------------------------
  //
  // ⚠ NO DURATION FIELD AND NO «до какого числа» — see the catalogue header. The date is a
  // START date, the operator's own earliest permitted value comes from
  // `GET /pa/accounts/capabilities`, and ending a block early is «Разблокировать».
  blockings: {
    navLabel: 'Блокировки',
    title: 'Блокировки',
    lead: 'Приостановка обслуживания по вашей заявке — «отпуск». Обслуживание возобновляется, когда вы его разблокируете.',
    statusLabel: 'Состояние',
    block: 'Заблокировать',
    unblock: 'Разблокировать',
    // Neither action is offered: the state is one only staff can change (приостановка за
    // неоплату, блокировка оператором, лимит трафика, отключена). The server refuses those
    // transitions, so the screen explains instead of offering.
    staffOnly: 'Это состояние учётной записи меняет только оператор. Обратитесь в поддержку по телефону, указанному в договоре.',
    // ⚠ WORDED TO BE TRUE OF BOTH STATUS-10 CASES. The wire carries no «never activated»
    // marker on this surface, so the sentence cannot claim the account IS activatable — a
    // brand-new one is (on «Мои аккаунты»), one staff switched off later is not, and the
    // portal cannot tell them apart from here.
    disabledAccount: 'Отключённая учётная запись не блокируется и не разблокируется. Новую учётную запись можно активировать на странице «Мои аккаунты».',
    blockTitle: 'Блокировка учётной записи',
    unblockTitle: 'Разблокировка учётной записи',
    accountLabel: 'Учётная запись',
    dateLabel: 'Дата',
    // ⚠ «в часовом поясе оператора» is not pedantry: the field's earliest value comes from
    // the server precisely because the browser does not know that zone, and around local
    // midnight the two are different days.
    dateHintBlock: 'Дата указана в часовом поясе оператора. Ближайшая дата — блокировка начнётся сразу; более поздняя — будет запланирована и до этого дня её можно отменить.',
    dateHintUnblock: 'Дата указана в часовом поясе оператора. Ближайшая дата — обслуживание возобновится сразу; более поздняя — разблокировка будет запланирована.',
    sameDayBlocked: 'Блокировка с текущего дня у этого оператора не оформляется. Ближайшая возможная дата — {{date}}.',
    sameDayUnblockBlocked: 'Разблокировка с текущего дня у этого оператора не оформляется. Ближайшая возможная дата — {{date}}.',
    dateRequired: 'Укажите дату',
    apply: 'Применить',
    cancel: 'Отмена',
    // Two outcomes, two sentences — `applied` is the field that distinguishes them.
    blockedNow: 'Обслуживание приостановлено по вашей заявке. Доступ закрывается в течение нескольких минут.',
    unblockedNow: 'Обслуживание возобновлено. Доступ открывается в течение нескольких минут.',
    blockScheduled: 'Блокировка запланирована на {{date}}. До этого дня её можно отменить.',
    unblockScheduled: 'Разблокировка запланирована на {{date}}. До этого дня её можно отменить.',
    // Appendix H §3.15, said once and honestly: the operator MAY configure a price for the
    // blocked period, and where he has not there is no charge at all. The portal cannot
    // know which, and inventing a figure here is exactly what this app forbids.
    chargeNote: 'Списания на время блокировки зависят от условий тарифа: оператор может назначить отдельную цену за период блокировки, а если она не назначена — списаний нет.',
    scheduledTitle: 'Запланировано',
    scheduledAt: 'с {{when}}',
    target: {
      block: 'Приостановка обслуживания',
      resume: 'Возобновление обслуживания',
    },
    // ⚠ A row whose `parent_scheduled_id` is set is the END DATE of the block above it.
    // Cancelling it would turn a timed «отпуск» into an indefinite one, so no «Отменить» is
    // rendered for it and this sentence says where to go instead.
    childNote: 'Это дата окончания блокировки, назначенной оператором. Отменить её отдельно нельзя — отменяется сама блокировка.',
    // ⚠ THE OTHER «нельзя», AND IT IS A DIFFERENT FACT. `can_cancel: false` on a row with NO
    // parent is a plan the OPERATOR made: it can be lifted, just not from here. The child note
    // above would be false about it (there is no block above it to cancel instead), and the
    // generic «обратитесь в поддержку» of `staffOnly` would not say why. Added 2026-08-12 with
    // the server's `can_cancel` verdict, which replaced this screen's own derivation.
    staffPlanNote: 'Это плановое изменение назначил оператор. Отменить его из личного кабинета нельзя — обратитесь в поддержку по телефону, указанному в договоре.',
    cancelPlan: 'Отменить',
    cancelPlanTitle: 'Отменить запланированное изменение?',
    cancelPlanText: 'Запланированное изменение не будет выполнено, а учётная запись останется в текущем состоянии.',
    cancelPlanConfirm: 'Отменить изменение',
    cancelPlanBack: 'Не отменять',
    planCancelled: 'Запланированное изменение отменено.',
    historyTitle: 'История блокировок',
    historyEmpty: 'Блокировок по этой учётной записи не было.',
    columnWhat: 'Что было',
    columnPeriod: 'Период',
    columnWho: 'Кем',
    historyPeriod: 'с {{from}} по {{to}}',
    historyOpen: 'с {{from}}, действует',
    bySelf: 'по вашей заявке',
    byStaff: 'оператором',
    noAccounts: 'На этом договоре нет учётных записей.',
  },

  // ---- §24 my devices (W4, F23, Amendment 02) ------------------------------------------
  devices: {
    navLabel: 'Мои устройства',
    title: 'Мои устройства',
    lead: 'Устройства, зарегистрированные на учётной записи. Новое устройство регистрируется само: подключите его к сети — откроется страница подключения, где нужно ввести логин и пароль учётной записи.',
    accountLabel: 'Учётная запись',
    limit: 'Занято мест: {{used}} из {{limit}}',
    limitHint: 'Сколько устройств можно зарегистрировать одновременно, задаёт оператор. Если мест не осталось, отвяжите ненужное устройство.',
    identityLabel: 'Как определяется устройство',
    macLabel: 'MAC-адрес',
    opt82CircuitLabel: 'Порт подключения (Option 82)',
    opt82RemoteLabel: 'Оборудование доступа (Option 82)',
    portLabel: 'Порт коммутатора',
    registeredLabel: 'Зарегистрировано',
    originLabel: 'Кем зарегистрировано',
    lastOnlineLabel: 'Последняя активность',
    lastOnlineNever: 'подключений не было',
    addressLabel: 'IP-адрес',
    // ⚠ THE TWO SENTENCES THAT MUST NOT MERGE. `address_known: false` means the lease read
    // did not happen — an empty address then says nothing at all about the device — while a
    // known-and-empty address means it holds none right now.
    addressUnknown: 'неизвестно',
    addressUnknownHint: 'Проверить адрес сейчас не удалось. Это не значит, что адреса нет.',
    addressNone: 'нет адреса',
    addressNoneHint: 'Сейчас устройству не выдан адрес — обычно потому, что оно не подключено.',
    // ⚠ THESE THREE KEYS ARE THE COLUMN'S OWN VALUES AND NOTHING ELSE — and the way they were
    // WRONG until 2026-08-12 is the lesson, so it is recorded rather than quietly corrected.
    // ~~`mac` / `mac_opt82` / `mac_trap`~~ matched no value this server can send. The real
    // vocabulary is fixed by a DATABASE CHECK — migration 0038's
    // `identity_form IN ('mac_option82', 'mac_trap_port', 'mac_only')` — and the handler
    // assigns it verbatim (`IdentityForm: string(d.IdentityForm)`), so a device card fell
    // through `deviceIdentityLabel`'s default and printed the raw ASCII slug «mac_option82»
    // to a Russian-speaking subscriber, on the ONE line that explains why his device is
    // recognised at all.
    //
    // THE PROVENANCE IS THE PART WORTH KEEPING: the wrong three were written from PROSE — the
    // Go doc comment on `paDeviceWire.IdentityForm` and this schema's own description in
    // `api/openapi.yaml` BOTH listed «mac», «mac_opt82», «mac_trap …», and the label table and
    // the harness fixture were built from that sentence instead of from the constraint. The
    // admin SPA, which reads the same column, had it right all along
    // (`apps/admin/src/shared/api/network/devices.ts` names the three as a closed union).
    // That is this project's own rule — verify a claim against CODE, never against another
    // document — reproduced once more, and the fixture is what hid it: `deviceFixture` carried
    // `mac_opt82`, so `DevicesScreen.test.tsx` asserted the Russian sentence rendered and was
    // GREEN on the wrong answer.
    //
    // ⚠ THE SET IS OPEN BY DESIGN (Amendment 02 §4.1 adds a form whenever a new qualifier can
    // be captured), which is why `deviceIdentityLabel` still falls back to the raw slug.
    //
    // ⚠ ~~The structural guard that would have caught this … is owed on the Go side.~~ DELIVERED
    // 2026-08-12 (the second review fix pass), both halves: `api/openapi.yaml` now declares the
    // `enum` on `PADevice.identity_form` (pinned to `devices.AllIdentityForms()` by
    // `TestEveryPAEnumIsPinnedBothWays`, which DISCOVERS enums and so had never looked here),
    // and `src/api/wire-spec.test.ts` pins THESE THREE KEYS to that enum in both directions —
    // through `deviceIdentityLabel`, the function the screen calls. A fourth form now fails by
    // name here on the day it is added, instead of reaching a subscriber as ASCII.
    identity: {
      mac_option82: 'по MAC-адресу и порту подключения (Option 82)',
      mac_trap_port: 'по MAC-адресу и порту коммутатора',
      mac_only: 'по MAC-адресу',
    },
    origin: {
      self: 'вами, на странице подключения',
      manager: 'оператором',
    },
    lease: {
      issued: 'выдан',
      static: 'закреплён за устройством',
      semi_spare: 'зарезервирован',
      expired: 'срок действия истёк',
    },
    deregister: 'Отвязать',
    deregisterTitle: 'Отвязать устройство?',
    deregisterFrees: 'Регистрация устройства будет удалена, а место в списке освободится.',
    deregisterNext: 'При следующем подключении устройство попадёт на страницу подключения, где его можно зарегистрировать снова — на эту же или на другую учётную запись.',
    // ⚠ WHICH OF THESE TWO IS SHOWN COMES FROM THE SERVER
    // (`device_deregister_cuts_session`) — ~~and on this build it is the first: there is
    // no `device.deregistered` event in this system, so nothing revokes the address or
    // cuts the NAS session~~ — and since 2026-08-17 (D265) it is the SECOND: every
    // un-binding path emits `device.unbound` and the network domain disconnects that
    // MAC's live session.
    //
    // BOTH STRINGS STAY. The screen must keep following the server rather than a
    // build's memory: that is why the flag exists, and it is why this flip cost no SPA
    // change beyond this comment. An installation running an older core still gets the
    // honest first sentence.
    deregisterKeepsSession: 'Текущее подключение при этом не прерывается: изменение подействует, когда устройство подключится в следующий раз.',
    deregisterCutsSession: 'Текущее подключение устройства будет прервано.',
    deregisterConfirm: 'Отвязать',
    deregisterBack: 'Отмена',
    deregistered: 'Устройство отвязано.',
    empty: 'На этой учётной записи нет зарегистрированных устройств. Подключите устройство к сети — откроется страница подключения, где его можно зарегистрировать.',
    noAccounts: 'На этом договоре нет учётных записей.',
  },

  // ---- §11 the ACCOUNT password (W4, F22) ----------------------------------------------
  //
  // ⚠ IT IS A DIFFERENT CREDENTIAL FROM «Изменение пароля», and every string here says so,
  // because a subscriber holds two (P7-A1) and this is the only self-service route to this
  // one: recovery rotates the PORTAL password and never touches an Account's.
  accountPassword: {
    navLabel: 'Пароль учётной записи',
    title: 'Пароль учётной записи',
    lead: 'Этот пароль спрашивают при регистрации устройства на странице подключения. Это не пароль личного кабинета — он меняется в разделе «Изменение пароля».',
    accountLabel: 'Учётная запись',
    currentLabel: 'Текущий пароль учётной записи',
    currentHint: 'Именно пароль учётной записи, а не пароль личного кабинета.',
    newLabel: 'Новый пароль',
    confirmLabel: 'Повтор пароля',
    mismatch: 'Пароли не совпадают',
    submit: 'Отправить',
    success: 'Пароль учётной записи изменён.',
    // The honest consequence, and it is «none»: RADIUS does not check this password, so
    // live sessions are unaffected and only the NEXT registration asks for the new value.
    successNote: 'Новый пароль понадобится при следующей регистрации устройства. Уже подключённые устройства продолжают работать.',
    disabled: 'Изменение пароля учётной записи в личном кабинете отключено оператором. Обратитесь в поддержку по телефону, указанному в договоре.',
    noAccounts: 'На этом договоре нет учётных записей.',
  },

  // ---- §26 technical support: the ticket list and «Новый запрос» (P2, F26, D215(b)) ----
  //
  // ⚠ «Поддержка» IN THE STRIP AND «Техническая поддержка» AS THE TITLE. The strip is one
  // horizontal line on a 360-px handset and now carries eighteen entries; the full phrase
  // would push three sections behind a scroll to say what one word says. The page itself has
  // room for the spec's own wording.
  tickets: {
    navLabel: 'Поддержка',
    title: 'Техническая поддержка',
    lead: 'Здесь можно написать в поддержку и посмотреть, что вам ответили. Все ваши запросы собраны в одном списке — договор для них не выбирается.',
    new: 'Новый запрос',
    newTitle: 'Новый запрос',
    classLabel: 'Класс запроса',
    classPlaceholder: 'Выберите класс',
    classRequired: 'Выберите класс запроса',
    subjectLabel: 'Тема сообщения',
    subjectPlaceholder: 'Коротко о проблеме',
    subjectRequired: 'Укажите тему',
    bodyLabel: 'Сообщение',
    bodyPlaceholder: 'Опишите, что происходит: когда началось, какие устройства затронуты',
    bodyRequired: 'Опишите проблему',
    // ⚠ THE SENTENCE THAT STANDS IN PLACE OF §26a's FILE PICKER. The two facts a picker needs
    // — whether this installation can store bytes at all, and the per-file ceiling — are
    // published only on the ticket's own detail, so the picker lives there, one press later.
    attachLater: 'Файл (например, снимок ошибки) можно прикрепить сразу после отправки — на странице запроса.',
    send: 'Отправить',
    cancel: 'Отмена',
    columnID: 'Номер',
    columnSubject: 'Заголовок',
    columnClass: 'Класс',
    columnStatus: 'Статус запроса',
    columnOrigin: 'Кто создал',
    columnUpdated: 'Обновлён',
    empty: 'У вас пока нет запросов. Если что-то не работает — напишите нам.',
    origin: {
      subscriber: 'вы',
      staff: 'оператор',
    },
    // The three author kinds, and the portal owns all three words: the wire carries a KIND
    // and never a name (§26b's «Администратор» is a label, not a person).
    author: {
      subscriber: 'Вы',
      staff: 'Оператор',
      system: 'Система',
    },
  },

  // ---- §26b one ticket: the thread, its files and the reply form ------------------------
  ticket: {
    back: '← Все запросы',
    idLabel: 'Номер запроса',
    classLabel: 'Класс запроса',
    originLabel: 'Кто создал',
    createdLabel: 'Создан',
    updatedLabel: 'Обновлён',
    filesTitle: 'Файлы запроса',
    noFiles: 'К самому запросу файлы не прикреплены.',
    attach: 'Прикрепить файл к запросу',
    attaching: 'Файл отправляется…',
    attached: 'Файл «{{name}}» прикреплён.',
    maxSize: 'Не больше {{limit}} на файл.',
    tooLarge: 'Файл слишком большой: допустимо не больше {{limit}}.',
    downloading: 'Загрузка…',
    threadTitle: 'Переписка',
    threadEmpty: 'В переписке пока ничего нет.',
    edited: 'изменено {{when}}',
    replyTitle: 'Новое сообщение',
    replyLabel: 'Сообщение',
    replyPlaceholder: 'Ответ или уточнение',
    replyFile: 'Файл к этому сообщению',
    bodyRequired: 'Напишите сообщение',
    send: 'Отправить',
    replied: 'Сообщение отправлено.',
    repliedWithFile: 'Сообщение и файл отправлены.',
    // ⚠ THE SEAM SENTENCE. A reply and its file are two requests in a fixed order (a file
    // hangs off a MESSAGE, which does not exist until the reply is posted), so when the
    // second fails the first has already reached the operator's queue — and telling him
    // «не отправлено» would make him type it again.
    replySentFileFailed: 'Сообщение отправлено, а файл прикрепить не удалось. Попробуйте прикрепить его ещё раз.',
    // Rendered when `status.allow_reply` is false — the SERVER's verdict, which a «Выполнен»
    // status does not by itself produce.
    replyClosed: 'По этому запросу ответы больше не принимаются. Если вопрос остался — создайте новый запрос.',
  },

  // ---- §28 news, and §18's category checklist lives on «Оповещения» --------------------
  news: {
    navLabel: 'Сообщения',
    title: 'Сообщения',
    lead: 'Объявления оператора: работы на сети, изменения условий, предложения.',
    categoryLabel: 'Категория',
    allCategories: 'Все категории',
    categoryOptionUnread: '{{name}} — новых: {{count}}',
    unreadOnly: 'Только непрочитанные',
    unreadTotal: 'Непрочитанных объявлений: {{count}}',
    new: 'Новое',
    mandatory: 'Обязательное',
    collapse: 'Свернуть',
    empty: 'Объявлений пока нет.',
    emptyUnread: 'Непрочитанных объявлений нет.',
    // A sentence and not a control: the checklist is §18's, where the matrix is.
    categoriesElsewhere: 'Какие категории объявлений вам показывать, можно выбрать в разделе «Оповещения».',
  },

  // ---- §18 alerts: the matrix, its amount and the category checklist (P2, F5, F6) -------
  //
  // ⚠ «ОПОВЕЩЕНИЯ» LEFT `strings.test.ts`'s FORBIDDEN WORD LIST IN THE SAME COMMIT AS THIS
  // BLOCK. It was there because the UI spec draws a notifications BELL in the global chrome
  // that this build does not render — and the word is now a SECTION with a screen behind it.
  // The bell is still not built and still must not appear; what changed is that the word is
  // no longer evidence of one.
  alerts: {
    navLabel: 'Оповещения',
    title: 'Оповещения',
    lead: 'О чём и как вам сообщать. Все уведомления по умолчанию выключены.',
    // The delivery precondition, said ONCE — never per checkbox.
    contactsTitle: 'Сообщения пока не смогут прийти',
    contactsNone: 'Ни телефон, ни email не подтверждены, поэтому уведомления не отправляются. Укажите и подтвердите контакт в разделе «Профиль».',
    contactsNoMobile: 'Телефон не подтверждён, поэтому SMS и сообщения в мессенджер не отправляются. Подтвердите его в разделе «Профиль».',
    contactsNoEmail: 'Email не подтверждён, поэтому письма не отправляются. Подтвердите его в разделе «Профиль».',
    categoriesTitle: 'Категории сообщений',
    categoriesHint: 'Здесь выбирается, какие объявления вам показывать в разделе «Сообщения». Изменения сохраняются сразу.',
    categoryMandatory: 'Отписаться нельзя. ',
    categoryUnread: 'новых: {{count}}',
    categoryLive: 'объявлений: {{count}}',
    noCategories: 'Оператор пока не создал категорий объявлений.',
    matrixTitle: 'Уведомления',
    noRows: 'Оператор не настроил ни одного уведомления.',
    save: 'Сохранить',
    saved: 'Настройки сохранены.',
    nothingToSave: 'Ничего не изменилось — сохранять нечего.',
    thresholdLabel: 'Сумма',
    thresholdHint: 'Предупреждать, когда баланс станет меньше этой суммы.',
    thresholdInvalid: 'Укажите сумму, например 100 или 100,50',
    thresholdNote: 'Сумма указывается в рублях. Ноль — это тоже настройка: предупреждение придёт, когда баланс станет отрицательным.',
    // The channel columns.
    channel: {
      sms: 'SMS',
      email: 'Email',
      messenger: 'Мессенджер',
    },
    channelOn: '{{channel}}: включено',
    channelOff: '{{channel}}: выключено',
    locked: {
      // The one locked reason this build can produce (§18's own condition).
      auto_generation_audience: 'Эту настройку сейчас изменить нельзя: у оператора не включено автоматическое формирование документов для подписанных абонентов.',
    },
    lockedGeneric: 'Эту настройку сейчас изменить нельзя.',
    // ⚠ THE ROW LABELS. The row SET is the server's — derived from its own column list — so
    // this table may be shorter than the matrix, and a key with no sentence here renders AS
    // THE KEY rather than disappearing. Thirteen today.
    row: {
      balance: 'Предупреждение о балансе менее',
      promise: 'Окончание обещанного платежа',
      statement: 'Ежемесячный отчёт о состоянии счёта',
      payment: 'Поступление платежа',
      invoice: 'Ежемесячный счёт',
      block_set: 'Установлена пользовательская блокировка',
      block_removed: 'Снята пользовательская блокировка',
      charge_failed: 'Не удалось списать абонентскую плату',
      dunning_failed: 'Не удалось списать с сохранённой карты',
      suspended: 'Обслуживание приостановлено',
      resumed: 'Обслуживание возобновлено',
      promise_missed: 'Обещанный платёж не погашен',
      // ⚠ «Напоминание о продлении» AND NOT «Скоро продление», which was the first wording:
      // «скоро» is one of `nav.test.tsx`'s placeholder tells, and a row label that differs
      // from a forbidden word only by its first letter's case is a guard waiting to fire on
      // somebody's harmless re-wording. §2.10's own phrasing is the reminder, not the «soon».
      renewal: 'Напоминание о продлении',
    },
  },

  // ---- §29 digital TV — 24TV, READ-ONLY (P2, S23(b), P6-T7/P6-T8) -----------------------
  //
  // ⚠ THERE IS NO WORD HERE FOR CONNECTING, DISCONNECTING OR REPLACING A PACKAGE, and there
  // must not be one: every 24TV Account on this installation is legacy-mode, so such a
  // control would change nothing. The vocabulary of this block is deliberately entirely
  // descriptive.
  tv: {
    navLabel: 'Цифровое ТВ',
    title: 'Цифровое ТВ',
    lead: 'Ваши пакеты 24ТВ и срок, на который они открыты.',
    noAccounts: 'На этом договоре нет учётных записей.',
    packetById: 'Пакет №{{id}}',
    comboLabel: 'Пакет по тарифу',
    noCombo: 'нет',
    noComboHint: 'Ваш тариф не включает пакет 24ТВ.',
    windowLabel: 'Открыто на текущий месяц',
    windowValue: 'с {{from}} по {{to}}',
    linkedLabel: 'Приложение 24ТВ',
    linkedYes: 'вход выполнялся, последний раз {{when}}',
    linkedNo: 'вы ещё не входили в приложение',
    purchasesTitle: 'Покупки пакетов',
    columnWhen: 'Когда',
    columnPacket: 'Пакет',
    columnAmount: 'Сумма',
    bundled: 'в составе тарифа',
    noPurchases: 'Покупок пакетов не было.',
    purchasesNote: 'Здесь показаны покупки, о которых знает биллинг. Пакеты, купленные внутри приложения 24ТВ, видны в самом приложении.',
    // The four states of the closed vocabulary, and the ADVICE for each.
    state: {
      unavailable: 'недоступно',
      not_entitled: 'нет пакета',
      active: 'открыто',
      activation_needed: 'нужно открыть приложение',
    },
    advice: {
      unavailable: 'Оператор пока не подключил 24ТВ. Это не связано с вашей учётной записью.',
      not_entitled: 'Ваш тариф не включает пакет 24ТВ. Пакеты, купленные внутри приложения 24ТВ, видны в самом приложении.',
      active: 'Подписка на текущий месяц открыта.',
      // ⚠ THE MONTHLY HOLE, and this sentence is the point of the whole section: the bundle
      // is pushed when 24ТВ calls in, i.e. when he OPENS THE APPLICATION, and the window is
      // the calendar month.
      activation_needed: 'Подписка на текущий месяц ещё не открыта. Откройте приложение 24ТВ и войдите в него — подписка откроется автоматически, доплачивать не нужно.',
    },
    adviceUnknown: 'Состояние подписки неизвестно. Если телевидение не работает, напишите в поддержку.',
    // The cross-note, rendered from `switching_managed_externally` and not from a comment.
    managedExternally: 'Смена пакетов 24ТВ выполняется в приложении 24ТВ, а не в личном кабинете.',
  },

  // ---- §7 the tariff change (P3, F16, T2(a)) --------------------------------------------
  //
  // ⚠ «ТАРИФНЫЙ ПЛАН» LEFT `nav.test.tsx`'s `UNBUILT_VOCABULARY` («Тарифн») IN THE SAME COMMIT
  // AS THIS BLOCK, which is the rule that file states about itself: the wave that makes a
  // forbidden word legitimate is the wave that must delete it. Until now the dashboard said
  // «Тариф» precisely because the longer word would have promised this section.
  //
  // ⚠ EVERY DATE SENTENCE HERE NAMES THE DAY THE MONEY MOVES, and none of them names the day
  // the subscriber typed. A date inside the window is SNAPPED FORWARD to the cycle boundary by
  // the server, so «тариф изменится 20 августа» over a charge that changes on the 1st is the
  // complaint the whole item exists to prevent.
  tariff: {
    navLabel: 'Тарифный план',
    title: 'Тарифный план',
    lead: 'Ваш тариф и переход на другой. Смена происходит с начала следующего расчётного периода — текущий период не пересчитывается.',
    accountLabel: 'Учётная запись',
    noAccounts: 'На этом договоре нет учётных записей.',
    currentTitle: 'Текущий тариф',
    noCurrent: 'Тариф не назначен. Обратитесь в поддержку по телефону, указанному в договоре.',
    nameLabel: 'Тарифный план',
    unnamedTariff: 'без названия',
    feeLabel: 'Абонентская плата',
    // ⚠ SAID BECAUSE THE FIGURE IS THE LIST PRICE. §3.11's скидка за сохранённую карту is
    // applied inside the расчёт, so the number here is not always what is списано.
    feeHint: 'Цена по прейскуранту на дату перехода. Скидки за способ оплаты учитываются при списании.',
    speedLabel: 'Скорость',
    speedNone: 'не задана',
    availableTitle: 'Тарифы, доступные для перехода',
    availableEmpty: 'Оператор пока не открыл переходы на другие тарифы.',
    // The two «no form» outcomes, and they are DIFFERENT facts: one is about this учётная
    // запись, the other about the оператор's настройки.
    boundaryUnknown: 'Сейчас нельзя определить дату следующего списания по этой учётной записи, поэтому смена тарифа недоступна. Обратитесь в поддержку по телефону, указанному в договоре.',
    noTransitions: 'Оператор не открыл переходы на другие тарифы для этой учётной записи. Сменить тариф можно через поддержку.',
    // fixes8 T-10 (customer 2026-09-24): while the operator's removal is pending the subscriber
    // may not change the tariff, and only the operator can cancel the removal.
    removalPending: 'Тариф будет снят {{date}}; смена тарифа недоступна. Отменить снятие может только оператор — обратитесь в поддержку.',
    effectiveNote: 'Выбранный тариф начнёт действовать {{date}} — с начала следующего расчётного периода.',
    columnAction: 'Действие',
    change: 'Сменить',
    cancel: 'Отмена',
    changeTitle: 'Смена тарифа',
    chosenLabel: 'Выбранный тариф',
    dateLabel: 'Дата перехода',
    // ⚠ THE SENTENCE THAT MAKES THE SNAP HONEST BEFORE HE PRESSES «Сменить».
    dateHint: 'Какую бы дату вы ни выбрали, тариф изменится {{date}} — в начале следующего расчётного периода. Поле можно оставить как есть.',
    changeNote: 'Текущий период не пересчитывается и не оплачивается заново: по новому тарифу спишется только следующий период.',
    scheduled: 'Переход на тариф «{{name}}» запланирован на {{date}}.',
    scheduledTitle: 'Запланированные смены тарифа',
    scheduledRow: 'Переход на тариф «{{name}}» — {{date}}',
    // Only an operator's row can carry a date on which the money does not move: this surface
    // writes boundary dates only.
    plannedByStaff: 'Запланировано оператором. Дата списания по новому тарифу может отличаться.',
    removeFromSchedule: 'Удалить из расписания',
    removeTitle: 'Удалить смену тарифа из расписания?',
    removeText: 'Запланированный переход не будет выполнен, тариф останется текущим. Запланировать переход снова можно в любой момент.',
    removeConfirm: 'Удалить',
    removeBack: 'Отмена',
    cancelled: 'Запланированная смена тарифа удалена.',
  },

  // ---- §17 additional services (P3, F17) -------------------------------------------------
  //
  // ⚠ «ДОПОЛНИТЕЛЬНЫЕ УСЛУГИ» LEFT `UNBUILT_VOCABULARY` IN THE SAME COMMIT AS THIS BLOCK.
  //
  // ⚠ AND THE ONE SENTENCE THAT MUST NOT BE SOFTENED IS `disconnectNoRefund`. The portal's
  // «Отключить» writes `valid_to = now` — it stops the service and returns nothing — while the
  // OPERATOR's neighbouring action refunds every kopeck the instance was ever charged. A
  // subscriber who presses this expecting his money back has been told the wrong one of the
  // two, and he cannot undo it.
  services: {
    navLabel: 'Дополнительные услуги',
    title: 'Дополнительные услуги',
    lead: 'Услуги, подключённые к учётной записи, и те, что можно подключить самостоятельно. Списание происходит сразу при подключении.',
    accountLabel: 'Учётная запись',
    noAccounts: 'На этом договоре нет учётных записей.',
    connectedTitle: 'Подключённые услуги',
    connectedEmpty: 'К этой учётной записи не подключено дополнительных услуг.',
    availableTitle: 'Доступные услуги',
    // ⚠ EMPTY IS A CONFIGURATION AND NOT A FAULT: `subscriber_orderable` defaults to false,
    // so an operator who has ticked nothing offers nothing — and the sentence says which of
    // the two it is rather than leaving a blank block.
    availableEmpty: 'Оператор пока не открыл услуги для самостоятельного подключения. Подключить услугу можно через поддержку.',
    availableHint: 'Стоимость списывается сразу: разовая услуга — единовременно, периодическая — подключение плюс оплата текущего периода.',
    historyTitle: 'История услуг',
    historyEmpty: 'Отключённых услуг по этой учётной записи не было.',
    columnName: 'Название услуги',
    columnTariff: 'Тарифный план',
    columnFrom: 'Подключена',
    columnTo: 'Действовала до',
    columnCost: 'Стоимость',
    columnConnectionCost: 'Стоимость подключения',
    // The ENGINE's figure, in its own column beside the прейскурант precisely so the two can
    // be seen to differ — which for услуга, входящая в абонентскую плату, they do.
    columnPayableNow: 'К оплате сейчас',
    columnStatus: 'Состояние',
    columnAction: 'Действие',
    // ⚠ «не указана» AND NEVER «0,00»: null means the tariff carries no cost row at this date,
    // and a zero would advertise as free a service the расчёт cannot charge at all.
    priceUnset: 'не указана',
    payableUnknown: 'не рассчитана',
    payableUnknownNote: 'Стоимость подключения сейчас рассчитать не удалось. Списание пройдёт по прейскуранту оператора — если сумма важна, уточните её в поддержке.',
    linkedNote: 'Услуга входит в абонентскую плату: при подключении списывается остаток текущего периода и стоимость подключения.',
    connectionCostInline: 'Подключение:',
    // ⚠ THE EXCLUSIVE BOUND, WORDED AND NOT COMPUTED. The date is stored and read back
    // verbatim, so the wording carries «не включая» — nothing in this app adds or subtracts a
    // day, because a date translated on one leg of a round trip is a field that does not read
    // back as it was written.
    untilExclusive: 'Действует до {{date}}, не включая этот день',
    quantityInline: 'Количество: {{count}}',
    selfAssigned: 'Подключена вами в личном кабинете',
    oneTimeNoDisconnect: 'разовая услуга — отключать нечего',
    connect: 'Подключить',
    order: 'Заказать',
    connectTitle: 'Подключение услуги',
    orderTitle: 'Заказ услуги',
    cancel: 'Отмена',
    quantityLabel: 'Количество',
    quantityInvalid: 'Укажите количество — целое число от 1',
    toDateLabel: 'Действует до',
    toDateHint: 'Необязательно. Услуга работает до этой даты, не включая её. Оставьте поле пустым, если срок не нужен.',
    chargeNow: 'Стоимость спишется с договора сразу после подтверждения.',
    connected: 'Услуга «{{name}}» подключена.',
    ordered: 'Услуга «{{name}}» заказана.',
    disconnect: 'Отключить',
    disconnectTitle: 'Отключить услугу?',
    disconnectStops: 'Услуга перестанет работать, дальнейшие списания по ней прекратятся.',
    // ⚠ THE SENTENCE THE WHOLE SCREEN IS ABOUT — see the block header.
    disconnectNoRefund: 'Уже оплаченный период не возвращается: отключение останавливает услугу, но не возвращает деньги.',
    disconnectAgain: 'Подключить услугу снова можно в списке доступных услуг, если оператор её предлагает.',
    disconnected: 'Услуга «{{name}}» отключена.',
    // ⚠ THE SERVER'S OWN FIGURE, PRINTED VERBATIM. It arrives as a decimal string inside the
    // error envelope and is NOT minor units, so it is never reformatted — and it is never
    // recomputed: «сколько внести» is the one figure this project has had wrong three times.
    shortfall: 'Не хватает {{amount}}. Пополните счёт на эту сумму и повторите подключение.',
    status: {
      not_enabled: 'ещё не началась',
      enabled_not_charged: 'работает, без списаний',
      enabled_charged: 'работает',
      disabled: 'отключена',
    },
  },

  // ---- §14 consumption statistics (P3, F13 / F15) ----------------------------------------
  //
  // ⚠ «СТАТИСТИКА» LEFT `UNBUILT_VOCABULARY` IN THE SAME COMMIT AS THIS BLOCK.
  //
  // ⚠ AND `unavailableTitle` / `unavailableText` ARE THE POINT OF THE WHOLE SECTION. A
  // detailed-store client with no configured DSN answers every read with an EMPTY SERIES AND A
  // NIL ERROR, so the obvious page renders «0 Б» on an installation that cannot see its own
  // traffic store — and a subscriber reads that as a fact about himself. These two sentences
  // are what the screen says instead, and there is no figure on the page beside them.
  statistics: {
    navLabel: 'Статистика',
    title: 'Статистика',
    lead: 'Сколько трафика прошло по учётной записи и сколько было подключений.',
    accountLabel: 'Учётная запись',
    noAccounts: 'На этом договоре нет учётных записей.',
    periodTitle: 'Период',
    // S32's own set. «Месяц» is the default and it is the SERVER's default too.
    preset: {
      day: 'День',
      week: 'Неделя',
      month: 'Месяц',
      quarter: 'Квартал',
      half_year: 'Полгода',
      year: 'Год',
    },
    fromLabel: 'С',
    toLabel: 'По',
    show: 'Показать',
    // ⚠ NO `rangeIncomplete` / `rangeReversed` HERE, and their absence is deliberate.
    // *(Deleted 2026-08-12, Phase 7 W5.)* They were this app's own wording for two refusals
    // the SERVER now words itself — «Укажите обе даты периода — и начало, и конец» and
    // «Конец периода раньше его начала: поменяйте даты местами», from `pa.period_incomplete`
    // and `pa.period_reversed` (`c5d9ab7`). They existed only because both refusals used to
    // arrive as `pa.invalid_period`, whose message asks for a YYYY-MM month. A local
    // catalogue entry beside a server that words the same verdict is the second wording that
    // drifts, and it is the client's copy nobody re-reads.
    // ⚠ THIS SENTENCE SAYS WHAT `earliest_date` IS, AND IT USED TO SAY SOMETHING ELSE.
    // ~~«Данные о трафике хранятся с {{date}}.»~~ STRUCK 2026-08-12 (Phase 7 combination
    // review): the figure is not a MEASUREMENT of anything. The server computes it as
    // `today − EffectiveRetentionDays()`, floored at the ClickHouse tables' own 1095-day TTL,
    // so on 2026-08-12 EVERY account on this installation read «хранятся с 13.08.2023» while
    // the oldest row in the store was from 2026-07-26. It is a CEILING on how far back a
    // question may reach, not a promise that anything is there.
    //
    // ⚠ AND AFTER CUTOVER THE OLD WORDING TURNS ACTIVELY HARMFUL, which is why it was worth
    // fixing now rather than at the first complaint. Ask for any pre-cutover month and the
    // store answers `data_available: true` with an empty series — it WAS read, it simply held
    // nothing then — so the screen says «За выбранный период трафика не зафиксировано» under a
    // header promising three years of history. That is a statement ABOUT THE SUBSCRIBER, made
    // out of a fact about when this system started writing, and it is precisely the confusion
    // the three-state design above exists to prevent.
    //
    // ~~The honest figure — the first day this store actually holds anything — IS NOT ON THE
    // WIRE. `PAStats` carries only `earliest_date`, so the portal cannot render it and must
    // not guess it.~~ STRUCK 2026-08-12 (the second review fix pass): the server now publishes
    // `data_since`, the MEASURED first day it holds a row for this Account, beside the computed
    // ceiling. The struck sentence stays because it states the rule that still binds — the
    // portal must not GUESS that day — and because a vanished sentence invites the same
    // «probably around the cutover» edit again. The three keys below are what renders it, and
    // `data_since: ""` renders NONE of them: it is «we cannot tell you when your history
    // begins», not a date, and `formatCivilDate('')` would print «с —».
    //
    // This sentence keeps saying what `earliest_date` IS, which is still worth saying: it is
    // the limit on what may be ASKED, and it is not the start of anybody's history.
    retentionHint:
      'Период раньше {{date}} запросить нельзя: столько хранятся данные о трафике. Это предельный срок хранения, а не дата, с которой есть записи по этой учётной записи.',
    totalsTitle: 'Итого за период',
    // ⚠ THE SERVER'S OWN WINDOW. A preset is resolved in the оператор's calendar, which the
    // browser does not know, so these two dates are the only truthful answer to «за что это».
    window: 'Период: {{from}} — {{to}}',
    totalLabel: 'Всего трафика',
    totalHint: 'Принято и передано вместе.',
    inputLabel: 'Принято',
    outputLabel: 'Передано',
    sessionsLabel: 'Подключений',
    onlineLabel: 'Время в сети',
    // ⚠ ONE TAB, and the count is a MEASUREMENT of the схема хранения rather than a decision:
    // «По часам», «По категориям» и «По адресам» have no data source at all. There is no
    // disabled tab and no «в разработке» — the screen renders exactly the views the server
    // says it can answer.
    tabDays: 'По дням',
    noViews: 'Оператор не публикует статистику потребления.',
    columnDay: 'Дата',
    columnInput: 'Принято',
    columnOutput: 'Передано',
    columnTotal: 'Всего',
    columnSessions: 'Подключений',
    columnTime: 'Время в сети',
    // ⚠ THIS IS THE «CONFIGURED AND EMPTY» CASE — the only one allowed to say «трафика не
    // было». The «we cannot see» case has its own two sentences below and no table at all,
    // and the «мы тогда ещё не собирали» case has the three keys after it.
    daysEmpty: 'За выбранный период трафика не зафиксировано.',
    // ⚠ THE SAME EMPTY TABLE, WEAKER SENTENCE, because part of the window predates the first
    // day this store holds anything for the учётная запись. The days before that were not
    // measured, so nothing may be said about them; the days after were, and about those this
    // is the truth. The date is not repeated here — `collectingHint` carries it, once.
    daysEmptyCollecting:
      'За те дни выбранного периода, по которым уже ведётся сбор, трафика не зафиксировано.',
    // ⚠ THE PARTIALLY-COLLECTED WINDOW: the figures are real for the tail and absent for the
    // head, and the totals cannot show the difference. Added 2026-08-12 with `data_since`.
    collectingHint:
      'Статистика по этой учётной записи собирается с {{date}}. За более ранние дни выбранного периода записей нет — их не собирали, и это не значит, что трафика не было.',
    // ⚠ THE WHOLLY-UNCOLLECTED WINDOW, which is what every pre-cutover period becomes: the
    // screen shows NO figure at all, because every zero on such a page is zero for a reason
    // that belongs to us. The pair mirrors `unavailableTitle` / `unavailableText`, deliberately
    // — a different cause, the same refusal to state it as a fact about the subscriber.
    collectingTitle: 'За этот период статистику ещё не собирали',
    collectingText:
      'Сбор статистики по этой учётной записи начался {{date}}, а выбранный период закончился раньше. Записей за него нет — это не значит, что трафика не было. Выберите период начиная с этой даты.',
    gapHint: 'Дни без трафика в таблице не показываются.',
    unavailableTitle: 'Мы не видим данные о трафике',
    unavailableText: 'Хранилище статистики не настроено или недостижимо, поэтому показать расход трафика сейчас нельзя. Это не значит, что трафика не было. Если статистика нужна, сообщите в поддержку.',
    durationHours: '{{hours}} ч {{minutes}} мин',
    durationMinutes: '{{minutes}} мин',
    durationShort: 'меньше минуты',
  },

  shell: {
    logout: 'Выйти',
    footerTitle: 'Личный кабинет абонента',
    // ⚠ THE FALLBACK, and it is only a fallback. `GET /pa/config` now publishes the
    // operator's own support telephone, email, company block and copyright line, and the
    // footer renders those when they are set. This sentence is what it says when the
    // operator has written nothing — inventing a telephone number is worse than pointing
    // at the one in the contract.
    footerSupport: 'По вопросам обслуживания обращайтесь в поддержку по телефону, указанному в договоре.',
    // The labels beside the operator's own contacts (W3, `GET /pa/config`).
    supportPhone: 'Поддержка',
    supportEmail: 'Электронная почта',
  },
} as const;

export default ru;
